import { Row, Col, Card, Table, Button, Input, Tag, Tooltip, Image, Switch, Tabs, Form, Space, Checkbox, Select, Modal } from "antd";
import { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import ConfirmationBox from "../../components/ConfirmationBox";
import apiPath from "../../constants/apiPath";
import moment from 'moment';
import notfound from "../../assets/images/no-img.png";
import { CloseOutlined, CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from "react-router-dom";
import { getSortOrder } from "../../constants/conts";
import { CrossIcon, EyeIcon, FilterX, PencilLineIcon, RefreshCcw, Upload, ViewIcon } from "lucide-react";
import BrandSelect from "../../components/select/BrandSelect";
import CategorySelect from "../../components/select/CategorySelect";
import SegmentSelect from "../../components/select/SegmentSelect";
import VehicleManufacturerSelect from "../../components/select/VehicleManufacturerSelect";
import VehicleSelect from "../../components/select/VehicleSelect";
import BulkUpdateLanguage from "./BulkUpdateLanguage";
import IncompleteProducts from "./IncompleteProducts";
import PendingVerification from "./PendingVerification";
const Search = Input.Search;

// Which backing collection/status each tab lists. Used to ask the API for the
// filter values actually present in that tab's data. `bulk-update` has no list.
const SECTION_BY_TAB = {
  incomplete: "incomplete",
  "pending-qc": "pending_verification",
  live: "live",
};

const EMPTY_FILTER_OPTIONS = {
  brands: [],
  categories: [],
  segments: [],
  vehicle_manufacturers: [],
  vehicles: [],
  vehicle_mapping_options: [],
  vehicle_added: [],
};

const VEHICLE_MAPPING_LABELS = {
  all: <span><Tag color="success">All Vehicles</Tag></span>,
  manufacturers: <span><Tag color="warning">By Manufacturers</Tag></span>,
  specific: <span><Tag color="processing">Specific Models</Tag></span>,
};

function Index() {

  const sectionName = "Product";
  const routeName = "product";

  const api = {
    status: apiPath.statusProduct,
    bulkStatus: apiPath.bulkStatusProduct,
    addEdit: apiPath.addEditProduct,
    list: apiPath.listProduct
  }

  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSearch = query.get("search") || "";
  const initialSort = query.get("sort") || "";
  const initialTab = query.get("tab") || "incomplete";

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  const [searchText, setSearchText] = useState(initialSearch);
  const [sortBy, setSortBy] = useState(initialSort);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [withoutVehicle, setWithoutVehicle] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [segmentId, setSegmentId] = useState(null);
  const [vehicleManufacturerId, setVehicleManufacturerId] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleAddedFilter, setVehicleAddedFilter] = useState(null);
  const [vehicleMappingFilter, setVehicleMappingFilter] = useState(null);
  // Status column filter (Live tab). Kept in state — not a ref — so applying it
  // re-renders and re-runs the fetch effect.
  const [statusFilter, setStatusFilter] = useState(null);
  // Bulk status change (Live tab)
  const [selectedLiveKeys, setSelectedLiveKeys] = useState([]);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);

  const { request } = useRequest()
  const [list, setList] = useState([])
  const { showConfirm } = ConfirmationBox()
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const debouncedSearchText = useDebounce(searchText, 500);

  // Options for the filter bar, derived from the rows of the active tab.
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const filterSection = SECTION_BY_TAB[activeTab];

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (_, { image_url }) => <Image width={60} height={60} src={image_url || notfound} />
    },
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "part_number"),
      render: (_, { part_number }) => part_number ? <span className="cap">{part_number}</span> : '-',
    },
    {
      title: "Vehicle Mapping",
      dataIndex: "vehicle_mapping_option",
      key: "vehicle_mapping_option",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "vehicle_mapping_option"),
      render: (_, { vehicle_mapping_option }) => {
        if (!vehicle_mapping_option) return '-';

        const colorMap = {
          'all': 'success',
          'manufacturers': 'warning',
          'specific': 'processing'
        };

        return (
          <Tag color={colorMap[vehicle_mapping_option] || 'default'} className="cap">
            {vehicle_mapping_option}
          </Tag>
        );
      }
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "price"),
      render: (_, { price }) => price ? `₹${price}` : '-',
    },
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "segment"),
      render: (_, { segment }) =>
        segment?.en_name ? <span className="cap">{segment?.en_name}</span> : '-',
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "brand"),
      render: (_, { brand }) =>
        brand?.en_name ? <span className="cap">{brand?.en_name}</span> : '-',
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "category"),
      render: (_, { category }) =>
        category?.en_name ? <span className="cap">{category?.en_name}</span> : '-',
    },
    {
      title: "Status",
      key: "is_active",
      filters: [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Inactive',
          value: false,
        },
      ],
      // Controlled, so Clear Filters / Refresh also reset the column's UI.
      filteredValue: statusFilter,
      render: (_, { is_active, _id }) => {
        const switchElement = (
          <Switch
            checked={is_active}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            loading={loading}
            disabled={activeTab !== 'live'}
            onChange={(checked) =>
              showConfirm({
                record: _id,
                path: api.status,
                // onLoading: () => setLoading(true),
                onSuccess: () => setRefresh((prev) => !prev),
                is_active: checked,
              })
            }
            className={is_active ? "custom-switch-checked" : "custom-switch-unchecked"}
          />
        );

        return activeTab !== 'live' ? (
          <Tooltip title="Status can only be changed in the Live Products tab">
            <span style={{ cursor: 'not-allowed' }}>{switchElement}</span>
          </Tooltip>
        ) : (
          switchElement
        );
      },
    },
    {
      title: "Created On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => {
        return (
          moment(created_at).format('DD-MMM-YYYY')
        );
      },
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => {
        return (
          <>
            {record.pending_edit ? (
              <Tooltip title="Edit Pending QC review" color="orange" key="pending-edit">
                <Button disabled style={{ color: '#fa8c16', borderColor: '#fa8c16' }}>
                  <PencilLineIcon size={15} />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={"Update " + sectionName} color={"purple"} key={"update"}>
                <Button onClick={() => { edit(record._id) }}>
                  <PencilLineIcon size={15} />
                </Button>
              </Tooltip>
            )}

            <Tooltip title={"View " + sectionName} color={"purple"} key={"view"}>
              <Button onClick={() => { view(record._id) }}>
                <EyeIcon size={15} />
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const view = (id) => {
    navigate(`/${routeName}/view/${id}${location.search}`);
  };

  const edit = (id) => {
    navigate(`/${routeName}/update/${id}${location.search}`);
  };

  useEffect(() => {
    const params = new URLSearchParams();

    if (activeTab && activeTab !== "incomplete") params.set("tab", activeTab);
    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (sortBy) params.set("sort", sortBy);

    navigate({ search: params.toString() }, { replace: true });
  }, [activeTab, pagination.current, pagination.pageSize, debouncedSearchText, sortBy, navigate]);

  const fetchData = (pagination, sortValue = '') => {
    const tabParam = `&without_vehicle_attach=${withoutVehicle ? 'true' : 'false'}`;

    request({
      url:
        api.list +
        `?status=${statusFilter ? statusFilter.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}` +
        tabParam +
        (brandId ? `&brand_id=${brandId}` : '') +
        (categoryId ? `&category_id=${categoryId}` : '') +
        (segmentId ? `&segment_id=${segmentId}` : '') +
        (vehicleManufacturerId ? `&vehicle_manufacturer_id=${vehicleManufacturerId}` : '') +
        (vehicleId ? `&vehicle_id=${vehicleId}` : '') +
        (vehicleAddedFilter ? `&vehicle_added=${vehicleAddedFilter}` : '') +
        (vehicleMappingFilter ? `&vehicle_mapping_option=${vehicleMappingFilter}` : ''),
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false)
        setList(data.data.list.docs)
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs
        }));
      },
      onError: (error) => {
        setLoading(false);
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to fetch products";
        ShowToast(errorMsg, Severty.ERROR);
      }
    })
  }

  const handleChange = (pag, filters, sorter) => {
    const { order, field } = sorter || {};

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    const nextStatusFilter = filters?.is_active?.length ? filters.is_active : null;
    const statusChanged = String(nextStatusFilter) !== String(statusFilter);

    setStatusFilter(nextStatusFilter);
    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      // A changed filter invalidates the current page offset.
      current: statusChanged ? 1 : (pag?.current || 1),
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value)
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const hanldeRefresh = () => {
    setSearchText('');
    setBrandId(null);
    setCategoryId(null);
    setSegmentId(null);
    setVehicleManufacturerId(null);
    setVehicleId(null);
    setVehicleAddedFilter(null);
    setVehicleMappingFilter(null);
    setStatusFilter(null);
    setPagination({ current: 1, pageSize: 20 });
    setRefresh(prev => !prev);
    setSortBy("");
    navigate({ search: "" }, { replace: true });
  };

  const clearFilters = () => {
    setSearchText('');
    setBrandId(null);
    setCategoryId(null);
    setSegmentId(null);
    setVehicleManufacturerId(null);
    setVehicleId(null);
    setVehicleAddedFilter(null);
    setVehicleMappingFilter(null);
    setStatusFilter(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefresh(prev => !prev);
  };

  // Keep the filter bar in sync with the data of the active tab: only values
  // that actually occur in the rows below are offered, already narrowed by the
  // other filters that are applied.
  useEffect(() => {
    if (!filterSection) return;

    setFilterOptionsLoading(true);

    request({
      url:
        apiPath.filterOptionsProduct +
        `?section=${filterSection}` +
        (debouncedSearchText ? `&search=${encodeURIComponent(debouncedSearchText)}` : '') +
        (filterSection === 'live' && statusFilter ? `&status=${statusFilter.join(',')}` : '') +
        (filterSection === 'live' && withoutVehicle ? '&without_vehicle_attach=true' : '') +
        (brandId ? `&brand_id=${brandId}` : '') +
        (categoryId ? `&category_id=${categoryId}` : '') +
        (segmentId ? `&segment_id=${segmentId}` : '') +
        (vehicleManufacturerId ? `&vehicle_manufacturer_id=${vehicleManufacturerId}` : '') +
        (vehicleId ? `&vehicle_id=${vehicleId}` : '') +
        (vehicleAddedFilter ? `&vehicle_added=${vehicleAddedFilter}` : '') +
        (vehicleMappingFilter ? `&vehicle_mapping_option=${vehicleMappingFilter}` : ''),
      method: 'GET',
      onSuccess: (data) => {
        setFilterOptionsLoading(false);

        const options = { ...EMPTY_FILTER_OPTIONS, ...(data?.data || {}) };
        setFilterOptions(options);

        // Drop a selection that is no longer present in the data (stale URL
        // state, or narrowed away by another filter); it would otherwise render
        // as a raw id and produce an empty table.
        const has = (available, id) => available.some((item) => item._id === id);

        if (brandId && !has(options.brands, brandId)) setBrandId(null);
        if (categoryId && !has(options.categories, categoryId)) setCategoryId(null);
        if (segmentId && !has(options.segments, segmentId)) setSegmentId(null);
        if (vehicleManufacturerId && !has(options.vehicle_manufacturers, vehicleManufacturerId)) setVehicleManufacturerId(null);
        if (vehicleId && !has(options.vehicles, vehicleId)) setVehicleId(null);
        if (vehicleMappingFilter && !options.vehicle_mapping_options.includes(vehicleMappingFilter)) setVehicleMappingFilter(null);
        if (vehicleAddedFilter && !options.vehicle_added.includes(vehicleAddedFilter)) setVehicleAddedFilter(null);
      },
      onError: () => setFilterOptionsLoading(false),
    });
  }, [filterSection, debouncedSearchText, withoutVehicle, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId, vehicleAddedFilter, vehicleMappingFilter, refresh])

  // A changed filter hides rows that may still be selected, and acting on
  // products the user can no longer see would be surprising. Pagination is
  // deliberately excluded so selecting across pages keeps working.
  useEffect(() => {
    setSelectedLiveKeys([]);
  }, [debouncedSearchText, statusFilter, withoutVehicle, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId, vehicleAddedFilter, vehicleMappingFilter])

  useEffect(() => {
    if (activeTab === 'live') {
      setLoading(true)
      fetchData(pagination, sortBy)
    }
  }, [refresh, debouncedSearchText, sortBy, statusFilter, pagination.current, pagination.pageSize, withoutVehicle, brandId, categoryId, segmentId, vehicleManufacturerId, vehicleId, vehicleAddedFilter, vehicleMappingFilter, activeTab])

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSelectedLiveKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Sets an explicit state for every selected product (the single-row switch
  // toggles instead, which would flip a mixed selection in both directions).
  const handleBulkStatus = (nextActive) => {
    if (selectedLiveKeys.length === 0) {
      ShowToast("Please select at least one product", Severty.ERROR);
      return;
    }

    Modal.confirm({
      title: nextActive ? "Activate Products" : "Deactivate Products",
      content: `Mark ${selectedLiveKeys.length} selected product(s) as ${nextActive ? "Active" : "Inactive"}?`,
      okText: "Confirm",
      centered: true,
      onOk: () => {
        setBulkStatusLoading(true);

        return request({
          url: api.bulkStatus,
          method: 'POST',
          data: { ids: selectedLiveKeys, is_active: nextActive },
          onSuccess: (data) => {
            setBulkStatusLoading(false);
            ShowToast(data?.message || "Status updated successfully", Severty.SUCCESS);
            setSelectedLiveKeys([]);
            setRefresh(prev => !prev);
          },
          onError: (error) => {
            setBulkStatusLoading(false);
            ShowToast(error?.response?.data?.message || "Failed to update status", Severty.ERROR);
          },
        });
      },
    });
  };

  const handleAddNew = () => {
    navigate(`/${routeName}/add${location.search}`);
  };

  const handleImportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/${routeName}/import${location.search}`);
  };

  return (
    <>
      <style>{`
        tr.row-mapping-all td:first-child { border-left: 4px solid #52c41a !important; }
        tr.row-mapping-manufacturers td:first-child { border-left: 4px solid #fa8c16 !important; }
        tr.row-mapping-specific td:first-child { border-left: 4px solid #2f54eb !important; }
      `}</style>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Part Number, English Name..."
                    style={{ minWidth: 280 }}
                  />

                  <div className="flex justify-end w-100 gap-2">
                    {(brandId || categoryId || segmentId || vehicleManufacturerId || vehicleId || searchText || vehicleAddedFilter || vehicleMappingFilter || statusFilter) && (
                      <Button
                        type="text"
                        onClick={clearFilters}
                        danger
                      >
                        <FilterX size={15} />
                        Clear Filters
                      </Button>
                    )}

                    <button
                      onClick={hanldeRefresh}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-indigo-500 text-indigo-500 rounded-md bg-transparent hover:bg-indigo-500 hover:text-white transition-all duration-200"
                    >
                      <RefreshCcw size={14} />
                      Refresh
                    </button>

                    {/* <Button
                      onClick={handleAddNew}
                      type="primary"
                    >
                      Add New {sectionName}
                    </Button>

                    <Button
                      htmlType="button"
                      onClick={handleImportClick}
                      type="primary"
                    >
                      <Upload className="w-[18px]" />
                      Import Bulk Products
                    </Button> */}
                  </div>
                </>
              }
            >

              <Row gutter={[24, 0]} align="middle border-bottom pb-2">
                <Col xs={24} sm={12} md={8}>
                  <BrandSelect
                    value={brandId}
                    onChange={setBrandId}
                    options={filterOptions.brands}
                    optionsLoading={filterOptionsLoading}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <CategorySelect
                    value={categoryId}
                    onChange={setCategoryId}
                    options={filterOptions.categories}
                    optionsLoading={filterOptionsLoading}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <SegmentSelect
                    value={segmentId}
                    onChange={setSegmentId}
                    options={filterOptions.segments}
                    optionsLoading={filterOptionsLoading}
                  />
                </Col>
              </Row>

              <Row gutter={[24, 0]} align="middle border-bottom pb-2">
                <Col xs={24} sm={12} md={8}>
                  <VehicleManufacturerSelect
                    value={vehicleManufacturerId}
                    onChange={(val) => {
                      setVehicleManufacturerId(val);
                      setVehicleId(null);
                    }}
                    options={filterOptions.vehicle_manufacturers}
                    optionsLoading={filterOptionsLoading}
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <VehicleSelect
                    value={vehicleId}
                    onChange={setVehicleId}
                    options={filterOptions.vehicles}
                    optionsLoading={filterOptionsLoading}
                  />
                </Col>

                <Col xs={24} sm={12} md={4}>
                  <Select
                    allowClear
                    placeholder="Vehicle Added"
                    value={vehicleAddedFilter}
                    onChange={(val) => {
                      setVehicleAddedFilter(val || null);
                      if (val === 'no') setVehicleMappingFilter(null);
                    }}
                    loading={filterOptionsLoading}
                    style={{ width: '100%', height: 40 }}
                    options={filterOptions.vehicle_added.map((value) => ({
                      value,
                      label: value === 'yes' ? 'Yes' : 'No',
                    }))}
                  />
                </Col>

                <Col xs={24} sm={12} md={4}>
                  <Select
                    allowClear
                    placeholder="Mapping Type"
                    value={vehicleMappingFilter}
                    onChange={(val) => setVehicleMappingFilter(val || null)}
                    disabled={vehicleAddedFilter === 'no'}
                    loading={filterOptionsLoading}
                    style={{ width: '100%', height: 40 }}
                    optionLabelProp="label"
                    options={filterOptions.vehicle_mapping_options.map((value) => ({
                      value,
                      label: VEHICLE_MAPPING_LABELS[value],
                    }))}
                  />
                </Col>
              </Row>

              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                destroyInactiveTabPane={false}
                items={[
                  {
                    key: 'incomplete',
                    label: 'Incomplete',
                    children: (
                      <IncompleteProducts
                        isActive={activeTab === 'incomplete'}
                        search={debouncedSearchText}
                        brandId={brandId}
                        categoryId={categoryId}
                        segmentId={segmentId}
                        vehicleManufacturerId={vehicleManufacturerId}
                        vehicleId={vehicleId}
                        vehicleAddedFilter={vehicleAddedFilter}
                        vehicleMappingFilter={vehicleMappingFilter}
                      />
                    ),
                  },
                  {
                    key: 'pending-qc',
                    label: 'Pending Verification',
                    children: (
                      <PendingVerification
                        onSwitchTab={handleTabChange}
                        isActive={activeTab === 'pending-qc'}
                        search={debouncedSearchText}
                        brandId={brandId}
                        categoryId={categoryId}
                        segmentId={segmentId}
                        vehicleManufacturerId={vehicleManufacturerId}
                        vehicleId={vehicleId}
                        vehicleAddedFilter={vehicleAddedFilter}
                        vehicleMappingFilter={vehicleMappingFilter}
                      />
                    ),
                  },
                  {
                    key: 'live',
                    label: 'Live Products',
                    children: (
                      <>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <button
                            disabled={selectedLiveKeys.length === 0 || bulkStatusLoading}
                            onClick={() => handleBulkStatus(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-green-500 text-green-600 rounded-md bg-transparent hover:bg-green-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <CheckOutlined /> Mark Active {selectedLiveKeys.length > 0 ? `(${selectedLiveKeys.length})` : ""}
                          </button>

                          <button
                            disabled={selectedLiveKeys.length === 0 || bulkStatusLoading}
                            onClick={() => handleBulkStatus(false)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-red-500 text-red-600 rounded-md bg-transparent hover:bg-red-500 hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <CloseOutlined /> Mark Inactive {selectedLiveKeys.length > 0 ? `(${selectedLiveKeys.length})` : ""}
                          </button>

                          {selectedLiveKeys.length > 0 && (
                            <Button type="text" onClick={() => setSelectedLiveKeys([])}>
                              Clear selection
                            </Button>
                          )}
                        </div>

                        <div style={{ marginBottom: 12 }}>
                          <Checkbox
                            checked={withoutVehicle}
                            onChange={(e) => {
                              setWithoutVehicle(e.target.checked);
                              setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                          >
                            Show only products without vehicle attachment
                          </Checkbox>
                        </div>
                        <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
                        <div className="table-responsive customPagination">
                          <Table
                            rowKey={(record) => record._id}
                            loading={loading}
                            columns={columns}
                            dataSource={list}
                            rowSelection={{
                              selectedRowKeys: selectedLiveKeys,
                              onChange: (keys) => setSelectedLiveKeys(keys),
                            }}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              responsive: true,
                              defaultPageSize: 20,
                              pageSizeOptions: ['10', '20', '30', '50', '100']
                            }}
                            onChange={handleChange}
                            className="ant-border-space"
                            onRow={(record) => {
                              const mapping = record.vehicle_mapping_option;
                              return mapping ? { className: `row-mapping-${mapping}` } : {};
                            }}
                          />
                        </div>
                      </>
                    )
                  },
                  {
                    key: 'bulk-update',
                    label: 'Bulk Update Languages',
                    children: <BulkUpdateLanguage />,
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Index;
